import { useState } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Segmented,
  SegmentedButton,
} from 'konsta/react'
import {
  TrendingUp,
  DollarSign,
  Users,
  DoorOpen,
  UserCog,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { formatCurrency, cn } from '@/lib/utils'

// --- Types ---

type TabKey = 'revenue' | 'players' | 'rooms' | 'commission'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'revenue', label: '收入' },
  { key: 'players', label: '玩家' },
  { key: 'rooms', label: '房間' },
  { key: 'commission', label: '佣金' },
]

// --- Mock Data ---

const revenueData = [
  { day: '2/5', amount: 3200 },
  { day: '2/6', amount: 4100 },
  { day: '2/7', amount: 3800 },
  { day: '2/8', amount: 4520 },
  { day: '2/9', amount: 5100 },
  { day: '2/10', amount: 6200 },
  { day: '2/11', amount: 5820 },
]

const playerData = [
  { day: '2/5', count: 89 },
  { day: '2/6', count: 102 },
  { day: '2/7', count: 95 },
  { day: '2/8', count: 112 },
  { day: '2/9', count: 134 },
  { day: '2/10', count: 156 },
  { day: '2/11', count: 127 },
]

const commissionData = [
  { agent: '王大明', amount: 450 },
  { agent: 'Sarah', amount: 380 },
  { agent: 'Lucy', amount: 290 },
  { agent: 'Tony', amount: 120 },
]

// --- Helpers ---

interface StatCardProps {
  label: string
  value: string
  icon: React.ElementType
  color: string
  trend?: 'up' | 'down' | 'flat'
  trendValue?: string
}

function StatCard({ label, value, icon: Icon, color, trend, trendValue }: StatCardProps) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5">
      <div className="flex items-center justify-between mb-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        {trend && trendValue && (
          <span
            className={cn(
              'flex items-center text-[11px] font-medium',
              trend === 'up' && 'text-[#34C759]',
              trend === 'down' && 'text-[#FF3B30]',
              trend === 'flat' && 'text-[#8E8E93]',
            )}
          >
            {trend === 'up' && <ArrowUpRight className="w-3 h-3 mr-0.5" />}
            {trend === 'down' && <ArrowDownRight className="w-3 h-3 mr-0.5" />}
            {trend === 'flat' && <Minus className="w-3 h-3 mr-0.5" />}
            {trendValue}
          </span>
        )}
      </div>
      <p className="text-xl font-bold text-black">{value}</p>
      <p className="text-xs text-[#8E8E93] mt-0.5">{label}</p>
    </div>
  )
}

// --- Chart tooltip styles ---

const tooltipStyle = {
  borderRadius: 12,
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
}

// --- Revenue Tab ---

function RevenueTab() {
  const totalRevenue = revenueData.reduce((sum, d) => sum + d.amount, 0)
  const avgRevenue = Math.round(totalRevenue / revenueData.length)
  const maxDay = revenueData.reduce((max, d) => (d.amount > max.amount ? d : max), revenueData[0])

  return (
    <>
      <Block className="!px-4 !mt-4">
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label="本週總收入"
            value={formatCurrency(totalRevenue * 100)}
            icon={DollarSign}
            color="#007AFF"
            trend="up"
            trendValue="+12.3%"
          />
          <StatCard
            label="日均收入"
            value={formatCurrency(avgRevenue * 100)}
            icon={TrendingUp}
            color="#34C759"
            trend="up"
            trendValue="+8.1%"
          />
        </div>
      </Block>

      <BlockTitle>每日收入趨勢</BlockTitle>
      <Block strong inset>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA" />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={{ stroke: '#E5E5EA' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => `$${v}`}
            />
            <Tooltip
              formatter={(value: number | undefined) => [`$${(value ?? 0).toLocaleString()}`, '收入']}
              labelStyle={{ color: '#8E8E93' }}
              contentStyle={tooltipStyle}
            />
            <Line
              type="monotone"
              dataKey="amount"
              stroke="#007AFF"
              strokeWidth={2.5}
              dot={{ fill: '#007AFF', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Block>

      <BlockTitle>最高收入日</BlockTitle>
      <Block className="!px-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FF9500]/15 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-[#FF9500]" />
          </div>
          <div>
            <p className="font-semibold">{maxDay.day}</p>
            <p className="text-sm text-[#8E8E93]">${maxDay.amount.toLocaleString()}</p>
          </div>
        </div>
      </Block>
    </>
  )
}

// --- Players Tab ---

function PlayersTab() {
  const peakDay = playerData.reduce((max, d) => (d.count > max.count ? d : max), playerData[0])
  const avgPlayers = Math.round(playerData.reduce((sum, d) => sum + d.count, 0) / playerData.length)
  const todayPlayers = playerData[playerData.length - 1].count

  return (
    <>
      <Block className="!px-4 !mt-4">
        <div className="grid grid-cols-3 gap-3">
          <StatCard
            label="最高峰值"
            value={`${peakDay.count}`}
            icon={Users}
            color="#FF3B30"
          />
          <StatCard
            label="日均玩家"
            value={`${avgPlayers}`}
            icon={Users}
            color="#007AFF"
          />
          <StatCard
            label="今日活躍"
            value={`${todayPlayers}`}
            icon={Users}
            color="#34C759"
            trend="down"
            trendValue="-18.6%"
          />
        </div>
      </Block>

      <BlockTitle>每日活躍玩家</BlockTitle>
      <Block strong inset>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={playerData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA" />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={{ stroke: '#E5E5EA' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value: number | undefined) => [`${value ?? 0} 人`, '活躍玩家']}
              labelStyle={{ color: '#8E8E93' }}
              contentStyle={tooltipStyle}
            />
            <Bar
              dataKey="count"
              fill="#007AFF"
              radius={[6, 6, 0, 0]}
              barSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      </Block>

      <BlockTitle>峰值日</BlockTitle>
      <Block className="!px-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/15 flex items-center justify-center">
            <Users className="w-5 h-5 text-[#FF3B30]" />
          </div>
          <div>
            <p className="font-semibold">{peakDay.day}</p>
            <p className="text-sm text-[#8E8E93]">{peakDay.count} 位活躍玩家</p>
          </div>
        </div>
      </Block>
    </>
  )
}

// --- Rooms Tab ---

function RoomsTab() {
  const roomStats = [
    { label: 'NLH 房間', value: '8', subtitle: '德州撲克', color: '#007AFF', icon: DoorOpen },
    { label: 'PLO 房間', value: '2', subtitle: 'Pot Limit Omaha', color: '#FF9500', icon: DoorOpen },
    { label: '平均佔用率', value: '72%', subtitle: '過去 7 天', color: '#34C759', icon: Users },
    { label: '總手牌數', value: '3,247', subtitle: '本週', color: '#AF52DE', icon: TrendingUp },
  ]

  return (
    <>
      <Block className="!px-4 !mt-4">
        <div className="grid grid-cols-2 gap-3">
          {roomStats.map((stat) => {
            const Icon = stat.icon
            return (
              <div
                key={stat.label}
                className="rounded-2xl bg-white p-4 shadow-sm border border-black/5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${stat.color}15` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: stat.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-black">{stat.value}</p>
                <p className="text-xs text-[#8E8E93] mt-0.5">{stat.label}</p>
                <p className="text-[11px] text-[#C7C7CC] mt-0.5">{stat.subtitle}</p>
              </div>
            )
          })}
        </div>
      </Block>

      <BlockTitle>房間詳情</BlockTitle>
      <List strongIos insetIos>
        <ListItem
          media={
            <div className="w-10 h-10 rounded-xl bg-[#007AFF]/15 flex items-center justify-center">
              <DoorOpen className="w-5 h-5 text-[#007AFF]" />
            </div>
          }
          title={<span className="font-medium">NLH $1/$2</span>}
          subtitle={<span className="text-[#8E8E93] text-[12px]">6/9 座位 &middot; 78% 佔用率</span>}
          after={<span className="text-[#34C759] text-sm font-medium">活躍</span>}
        />
        <ListItem
          media={
            <div className="w-10 h-10 rounded-xl bg-[#007AFF]/15 flex items-center justify-center">
              <DoorOpen className="w-5 h-5 text-[#007AFF]" />
            </div>
          }
          title={<span className="font-medium">NLH $2/$5</span>}
          subtitle={<span className="text-[#8E8E93] text-[12px]">8/9 座位 &middot; 89% 佔用率</span>}
          after={<span className="text-[#34C759] text-sm font-medium">活躍</span>}
        />
        <ListItem
          media={
            <div className="w-10 h-10 rounded-xl bg-[#FF9500]/15 flex items-center justify-center">
              <DoorOpen className="w-5 h-5 text-[#FF9500]" />
            </div>
          }
          title={<span className="font-medium">PLO $1/$2</span>}
          subtitle={<span className="text-[#8E8E93] text-[12px]">4/6 座位 &middot; 67% 佔用率</span>}
          after={<span className="text-[#34C759] text-sm font-medium">活躍</span>}
        />
        <ListItem
          media={
            <div className="w-10 h-10 rounded-xl bg-[#007AFF]/15 flex items-center justify-center">
              <DoorOpen className="w-5 h-5 text-[#007AFF]" />
            </div>
          }
          title={<span className="font-medium">NLH $5/$10</span>}
          subtitle={<span className="text-[#8E8E93] text-[12px]">3/9 座位 &middot; 33% 佔用率</span>}
          after={<span className="text-[#FF9500] text-sm font-medium">低佔用</span>}
        />
      </List>
    </>
  )
}

// --- Commission Tab ---

function CommissionTab() {
  const totalCommission = commissionData.reduce((sum, d) => sum + d.amount, 0)

  return (
    <>
      <Block className="!px-4 !mt-4">
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label="本週總佣金"
            value={`$${totalCommission.toLocaleString()}`}
            icon={DollarSign}
            color="#FF9500"
            trend="up"
            trendValue="+5.2%"
          />
          <StatCard
            label="活躍代理"
            value={`${commissionData.length}`}
            icon={UserCog}
            color="#007AFF"
          />
        </div>
      </Block>

      <BlockTitle>代理佣金排名</BlockTitle>
      <Block strong inset>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={commissionData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: '#8E8E93' }}
              axisLine={{ stroke: '#E5E5EA' }}
              tickLine={false}
              tickFormatter={(v: number) => `$${v}`}
            />
            <YAxis
              type="category"
              dataKey="agent"
              tick={{ fontSize: 12, fill: '#000' }}
              axisLine={false}
              tickLine={false}
              width={60}
            />
            <Tooltip
              formatter={(value: number | undefined) => [`$${(value ?? 0).toLocaleString()}`, '佣金']}
              labelStyle={{ color: '#8E8E93' }}
              contentStyle={tooltipStyle}
            />
            <Bar
              dataKey="amount"
              fill="#FF9500"
              radius={[0, 6, 6, 0]}
              barSize={24}
            />
          </BarChart>
        </ResponsiveContainer>
      </Block>

      <BlockTitle>佣金明細</BlockTitle>
      <List strongIos insetIos>
        {commissionData.map((item, index) => (
          <ListItem
            key={item.agent}
            media={
              <div
                className={cn(
                  'w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm',
                  index === 0 && 'bg-[#FFD700]',
                  index === 1 && 'bg-[#C0C0C0]',
                  index === 2 && 'bg-[#CD7F32]',
                  index > 2 && 'bg-[#8E8E93]',
                )}
              >
                #{index + 1}
              </div>
            }
            title={<span className="font-medium">{item.agent}</span>}
            subtitle={
              <span className="text-[#8E8E93] text-[12px]">
                佔比 {Math.round((item.amount / totalCommission) * 100)}%
              </span>
            }
            after={
              <span className="text-[15px] font-semibold text-[#FF9500] tabular-nums">
                ${item.amount}
              </span>
            }
          />
        ))}
      </List>
    </>
  )
}

// --- Main Component ---

export function AdminReportsScreen() {
  const [activeTab, setActiveTab] = useState<TabKey>('revenue')

  return (
    <>
      <Navbar title="報表" />

      {/* Tab Selector */}
      <Block className="!mt-4 !mb-0">
        <Segmented strong rounded>
          {tabs.map((tab) => (
            <SegmentedButton
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      {/* Tab Content */}
      {activeTab === 'revenue' && <RevenueTab />}
      {activeTab === 'players' && <PlayersTab />}
      {activeTab === 'rooms' && <RoomsTab />}
      {activeTab === 'commission' && <CommissionTab />}

      {/* Bottom padding */}
      <div className="h-8" />
    </>
  )
}
